/**
 * Live quick-meeting state for CRM comment threads (Google Meet via Calendar).
 */
import { db } from './firebase';
import {
  createInstantMeetEvent,
  isGoogleCalendarConnected,
} from './googleCalendarService';
import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';

export { isActiveCommentThreadMeeting, COMMENT_THREAD_MEETING_STALE_MS } from './commentThreadMeetingActive';

export interface CommentThreadMeeting {
  threadKey: string;
  meetUrl: string;
  eventId: string;
  calendarId: string;
  hostUid: string;
  hostName: string;
  title: string;
  dialInPin?: string;
  createdAt: Date | null;
  endsAt: Date | null;
  endedAt: Date | null;
  projectId?: string;
}

function meetingDocId(threadKey: string): string {
  return (threadKey || '_unknown').slice(0, 700);
}

function meetingDoc(threadKey: string) {
  return doc(db, 'commentThreadMeetings', meetingDocId(threadKey));
}

export function subscribeCommentThreadMeeting(
  threadKey: string,
  callback: (meeting: CommentThreadMeeting | null) => void,
): () => void {
  if (!threadKey) {
    callback(null);
    return () => {};
  }
  return onSnapshot(
    meetingDoc(threadKey),
    (snap) => {
      if (!snap.exists()) {
        callback(null);
        return;
      }
      const v = snap.data();
      const ts = (raw: unknown): Date | null => {
        if (raw && typeof raw === 'object' && 'toDate' in raw) {
          return (raw as { toDate: () => Date }).toDate();
        }
        return null;
      };
      callback({
        threadKey,
        meetUrl: String(v.meetUrl || ''),
        eventId: String(v.eventId || ''),
        calendarId: String(v.calendarId || ''),
        hostUid: String(v.hostUid || ''),
        hostName: String(v.hostName || 'Someone'),
        title: String(v.title || 'Quick meeting'),
        dialInPin: v.dialInPin ? String(v.dialInPin) : undefined,
        createdAt: ts(v.createdAt),
        endsAt: ts(v.endsAt),
        endedAt: ts(v.endedAt),
        projectId: v.projectId ? String(v.projectId) : undefined,
      });
    },
    (err) => {
      console.warn('[commentThreadMeetingService] subscribe failed', err);
      callback(null);
    },
  );
}

export async function startCommentThreadMeeting(params: {
  threadKey: string;
  hostUid: string;
  hostName: string;
  title: string;
  description?: string;
  projectId?: string;
  durationMinutes?: number;
}): Promise<CommentThreadMeeting> {
  const { threadKey, hostUid, hostName, title } = params;
  if (!threadKey || !hostUid) {
    throw new Error('Missing thread or host');
  }

  const connected = await isGoogleCalendarConnected(hostUid);
  if (!connected) {
    throw new Error('Google Calendar not connected');
  }

  const instant = await createInstantMeetEvent(hostUid, {
    title,
    description: params.description,
    durationMinutes: params.durationMinutes ?? 30,
  });

  const payload = {
    meetUrl: instant.meetUrl,
    eventId: instant.eventId,
    calendarId: instant.calendarId,
    hostUid,
    hostName,
    title,
    dialInPin: instant.dialInPin ?? null,
    createdAt: serverTimestamp(),
    endsAt: Timestamp.fromDate(instant.end),
    endedAt: null,
    projectId: params.projectId ?? null,
  };

  await setDoc(meetingDoc(threadKey), payload, { merge: false });

  return {
    threadKey,
    meetUrl: instant.meetUrl,
    eventId: instant.eventId,
    calendarId: instant.calendarId,
    hostUid,
    hostName,
    title,
    dialInPin: instant.dialInPin,
    createdAt: new Date(),
    endsAt: instant.end,
    endedAt: null,
    projectId: params.projectId,
  };
}

export async function endCommentThreadMeeting(
  threadKey: string,
  hostUid: string,
): Promise<void> {
  if (!threadKey || !hostUid) return;
  await updateDoc(meetingDoc(threadKey), {
    endedAt: serverTimestamp(),
  });
}

export { isGoogleCalendarConnected };
